// Bandeau « Aujourd'hui » — 3 chiffres + priorité #1 avec Next Best
// Action. Alimenté par GET /leads-engine/today (100% données persistées,
// aucun run pipeline coûteux).
import { Sparkles, ArrowRight } from "lucide-react";
import {
  NAVY, CHAMPAGNE, IVORY, GOLD, SLATE,
} from "@/components/altiora/PremiumUI";
import {
  humanStatus, statusPillStyle, contactabilityBadge, tierBadge,
} from "@/lib/altiora-leads-i18n";

export default function TodayHero({ today, alerts, onPreparePriority }) {
  const p1 = today?.priority_one;
  const nba = today?.priority_one_next_action;
  const alertsCount = alerts?.length || 0;
  const priorityCount = today?.priorities_count || 0;
  const followupCount = today?.followups_count || 0;
  const hasAny = priorityCount || followupCount || alertsCount;

  return (
    <section data-testid="today-hero"
             className="mt-6 rounded-2xl p-6 md:p-8"
             style={{ background: NAVY, color: IVORY }}>
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4" style={{ color: GOLD }} />
        <h2 className="text-[10px] font-mono uppercase tracking-[0.28em]"
            style={{ color: GOLD }}>Aujourd'hui</h2>
      </div>

      {/* Compteurs — n'affiche jamais un chiffre inventé */}
      <div className="mt-4 grid grid-cols-3 gap-3 md:gap-6"
           data-testid="today-counters">
        <div data-testid="today-priorities">
          <div className="font-serif text-[38px] md:text-[46px] tabular-nums">
            {priorityCount}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em]"
               style={{ color: SLATE }}>
            Priorité{priorityCount > 1 ? "s" : ""}
          </div>
        </div>
        <div data-testid="today-followups">
          <div className="font-serif text-[38px] md:text-[46px] tabular-nums">
            {followupCount}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em]"
               style={{ color: SLATE }}>
            Suivi{followupCount > 1 ? "s" : ""}
          </div>
        </div>
        <div data-testid="today-alerts">
          <div className="font-serif text-[38px] md:text-[46px] tabular-nums">
            {alertsCount}
          </div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em]"
               style={{ color: SLATE }}>
            Signaux importants
          </div>
        </div>
      </div>

      {/* Priorité #1 — seulement si le backend en a une */}
      {p1 ? (
        <div className="mt-6 rounded-xl p-5"
             data-testid="priority-one-card"
             style={{ background: "rgba(251,246,234,0.08)",
                      border: `1px solid ${CHAMPAGNE}55` }}>
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] mb-2"
               style={{ color: GOLD }}>Votre priorité #1</div>
          <div className="flex flex-wrap items-start gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-serif text-[22px] md:text-[26px] leading-tight">
                {p1.entity_name}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                      style={{
                        background: tierBadge(p1.tier).bg,
                        color: tierBadge(p1.tier).color,
                      }}>
                  {tierBadge(p1.tier).label}
                </span>
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                      style={{
                        background: statusPillStyle(p1.status).bg,
                        color: statusPillStyle(p1.status).color,
                      }}>
                  {humanStatus(p1.status)}
                </span>
                <span className="text-[11px]" style={{ color: GOLD }}>
                  {contactabilityBadge(p1?.contactability?.status).emoji}{" "}
                  {contactabilityBadge(p1?.contactability?.status).label}
                </span>
              </div>
              <p className="mt-3 text-[13px]" style={{ color: IVORY }}>
                {p1.why_now && p1.why_now !== "not_found"
                  ? p1.why_now
                  : "Aucun signal daté récent."}
              </p>
              {nba && (
                <div className="mt-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.22em]"
                       style={{ color: SLATE }}>
                    Action recommandée
                  </div>
                  <p className="text-[14px] mt-1" style={{ color: IVORY }}>
                    <strong>{nba.label}</strong>
                    {nba.hint && (
                      <span className="text-[12px] block mt-1"
                            style={{ color: SLATE }}>{nba.hint}</span>
                    )}
                  </p>
                </div>
              )}
            </div>
            <button
              data-testid="priority-one-cta"
              onClick={() => onPreparePriority?.(p1)}
              className="text-[12px] px-4 py-2 rounded-full flex items-center gap-2"
              style={{ background: GOLD, color: NAVY }}>
              Préparer l'action <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      ) : hasAny ? (
        <p className="mt-6 text-[13px]" style={{ color: SLATE }}>
          Aucune priorité active à traiter — utilisez « Détecter les opportunités »
          ci-dessous pour scanner les registres publics.
        </p>
      ) : (
        <p className="mt-6 text-[13px]" style={{ color: SLATE }}>
          Aucune donnée pour l'instant — lancez une première détection pour
          alimenter votre journée.
        </p>
      )}
    </section>
  );
}
